import type { ToolCallError } from "@repo/app-types/api/interviewLivekitAgent";
import {
  IMPLICIT_END_CALL_TOOL_DISPLAY_NAME,
  IMPLICIT_END_CALL_TOOL_ID,
} from "@repo/app-types/entities/implicitEndCallTool";
import { KNOWLEDGE_RETRIEVAL_TOOL_ID } from "@repo/app-types/entities/knowledgeRetrievalTool";
import {
  checkRecordingToolActivityFailed,
  getCallTransferToolDestination,
  getKnowledgeRetrievalToolDocumentTitle,
  type AdminApiRecordingToolActivity,
} from "@repo/app-types/entities/recordingToolActivity";
import type { CustomerInteractionAgentStep } from "@repo/app-types/entities/teammatePerformanceSummary";
import { ToolType, type Tool } from "@repo/app-types/entities/tool";
import {
  TOOL_RESULT_QUERY_TOOL_DISPLAY_NAME,
  TOOL_RESULT_QUERY_TOOL_ID,
} from "@repo/app-types/entities/tools/toolResultQuery";
import { safeParseJSON } from "@repo/app-types/helpers/utilities";
import { DateTime } from "luxon";
import type { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { ToolCallActivityLine } from "../../components/transcript/toolCallActivityLine.js";
import { ToolCallExecutionDetailsView } from "../tools/components/toolCallExecutionDetails.js";

interface ToolActivityBlockProps {
  activity: AdminApiRecordingToolActivity;
  tools: readonly Tool[] | undefined;
}

export const ToolActivityBlock: FC<ToolActivityBlockProps> = (props) => {
  const { activity, tools } = props;
  const tool = tools?.find((t) => t.id === activity.tool_id);
  const isFailed = checkRecordingToolActivityFailed(activity.output);
  const title = renderToolActivityTitle(activity.tool_id, tool);
  const formattedTime = DateTime.fromISO(activity.created_at).toLocaleString(
    DateTime.DATETIME_FULL_WITH_SECONDS,
  );

  return (
    <ToolCallActivityLine
      status={isFailed ? "failed" : "success"}
      label={<ToolActivityDisplay activity={activity} tool={tool} />}
      title={title}
      formattedTime={formattedTime}
    >
      <RecordingToolActivityDetails activity={activity} tool={tool} />
    </ToolCallActivityLine>
  );
};

interface AgentStepToolCallBlockProps {
  step: Extract<CustomerInteractionAgentStep, { type: "tool_called" }>;
  tools: readonly Tool[] | undefined;
}

/** A tool the agent called between texts. */
export const AgentStepToolCallBlock: FC<AgentStepToolCallBlockProps> = (
  props,
) => {
  const { step, tools } = props;
  const isError = step.is_error ?? false;
  // The model calls a configured tool by its slugged name; platform tools have no id.
  const name =
    tools?.find((tool) => tool.id === step.tool_id)?.name ?? step.name;
  const formattedTime = DateTime.fromISO(step.at).toLocaleString(
    DateTime.DATETIME_FULL_WITH_SECONDS,
  );

  return (
    <ToolCallActivityLine
      status={isError ? "failed" : "success"}
      label={
        <>
          <span className="font-medium">{name}</span>{" "}
          {isError ? "tool failed" : "tool used"}
        </>
      }
      title={name}
      formattedTime={formattedTime}
    >
      <div>
        <h4 className="font-semibold">Input</h4>
        <pre className="mt-1 rounded-sm bg-gray-100 p-2 text-xs whitespace-pre-wrap">
          {formatJSONish(step.arguments)}
        </pre>
      </div>
      <div>
        <h4 className={twMerge("font-semibold", isError && "text-red-700")}>
          {isError ? "Error" : "Output"}
        </h4>
        <pre className="mt-1 rounded-sm bg-gray-100 p-2 text-xs whitespace-pre-wrap">
          {formatJSONish(step.result)}
        </pre>
      </div>
    </ToolCallActivityLine>
  );
};

const ToolActivityDisplay: FC<{
  activity: AdminApiRecordingToolActivity;
  tool: Tool | undefined;
}> = ({ activity, tool }): ReactNode => {
  const isFailed = checkRecordingToolActivityFailed(activity.output);

  // There is no "tool" for the built-in knowledge retrieval tool, just an ID.
  if (activity.tool_id === KNOWLEDGE_RETRIEVAL_TOOL_ID) {
    if (isFailed) {
      return "Knowledge base document retrieval failed";
    }

    const documentTitle = getKnowledgeRetrievalToolDocumentTitle(activity);

    if (!documentTitle) {
      return "Unknown knowledge base document retrieved";
    }

    return (
      <>
        <span className="font-medium">{documentTitle}</span> knowledge base
        document retrieved
      </>
    );
  }

  if (activity.tool_id === IMPLICIT_END_CALL_TOOL_ID) {
    return (
      <>
        <span className="font-medium">
          {IMPLICIT_END_CALL_TOOL_DISPLAY_NAME}
        </span>{" "}
        {isFailed ? "tool failed to end the call" : "tool ended the call"}
      </>
    );
  }

  if (activity.tool_id === TOOL_RESULT_QUERY_TOOL_ID) {
    return (
      <>
        <span className="font-medium">
          {TOOL_RESULT_QUERY_TOOL_DISPLAY_NAME}
        </span>{" "}
        {isFailed ? "tool failed" : "tool queried a stored result"}
      </>
    );
  }

  if (!tool) {
    return isFailed ? "Unknown tool failed" : "Unknown tool used";
  }

  if (isFailed) {
    const status = activity.output.error?.response_status;
    const failure =
      tool.type === ToolType.CALL_TRANSFER
        ? "tool failed to transfer the call"
        : tool.type === ToolType.CALL_END
          ? "tool failed to end the call"
          : "tool failed";
    return (
      <>
        <span className="font-medium">{tool.name}</span> {failure}
        {status ? ` with status ${status}` : null}
      </>
    );
  }

  switch (tool.type) {
    case ToolType.CALL_TRANSFER: {
      const destination = getCallTransferToolDestination(activity);

      return (
        <>
          <span className="font-medium">{tool.name}</span> tool transferred to{" "}
          {destination || "unknown destination"}
        </>
      );
    }
    case ToolType.CALL_END:
      return (
        <>
          <span className="font-medium">{tool.name}</span> tool ended the call
        </>
      );

    default:
      return (
        <>
          <span className="font-medium">{tool.name}</span> tool used
        </>
      );
  }
};

interface RecordingToolActivityDetailsProps {
  activity: AdminApiRecordingToolActivity;
  tool: Tool | undefined;
}

const RecordingToolActivityDetails: FC<RecordingToolActivityDetailsProps> = (
  props,
) => {
  const { activity, tool } = props;
  const toolId = activity.tool_id;
  const isFailed = checkRecordingToolActivityFailed(activity.output);
  const { error, execution_details: executionDetails } = activity.output;

  const renderToolContent = () => {
    if (toolId === KNOWLEDGE_RETRIEVAL_TOOL_ID) {
      if (isFailed) {
        return <ToolActivityInput activity={activity} />;
      }

      const documentTitle = getKnowledgeRetrievalToolDocumentTitle(activity);

      return (
        <div>
          <h4 className="font-semibold">Title</h4>
          <p>{documentTitle || "Unknown"}</p>
        </div>
      );
    }

    if (toolId === IMPLICIT_END_CALL_TOOL_ID) {
      return (
        <p>
          {isFailed
            ? "The agent tried to hang up the call, but it did not go through."
            : "The agent hung up the call."}
        </p>
      );
    }

    if (toolId === TOOL_RESULT_QUERY_TOOL_ID) {
      return (
        <>
          <div>
            <h4 className="font-semibold">Query</h4>
            <p>{formatQuery(activity.input.tool_arguments.query)}</p>
          </div>

          <div>
            <h4 className="font-semibold">Answer</h4>
            <pre className="mt-1 rounded-sm bg-gray-100 p-2 text-xs whitespace-pre-wrap">
              {activity.output.content || "(No answer.)"}
            </pre>
          </div>
        </>
      );
    }

    switch (tool?.type) {
      case ToolType.CALL_TRANSFER: {
        if (isFailed) {
          return (
            <p>
              The agent tried to transfer the call, but it did not go through.
            </p>
          );
        }

        const destination = getCallTransferToolDestination(activity);

        return (
          <div>
            <h4 className="font-semibold">Transfer destination</h4>
            <p>{destination || "Unknown"}</p>
          </div>
        );
      }

      case ToolType.CALL_END:
        return (
          <p>
            {isFailed
              ? "The agent tried to end the call, but it did not go through."
              : "The call ended or was transferred back to the original phone provider."}
          </p>
        );

      case ToolType.CUSTOM:
      case ToolType.CODE:
      case ToolType.WEB_DOCUMENT_RESEARCH:
        return (
          <>
            <ToolActivityInput activity={activity} />

            {/* A call that threw never answered the agent at all. */}
            {activity.output.content !== undefined ? (
              <div>
                <h4 className="font-semibold">Output</h4>
                <pre className="mt-1 rounded-sm bg-gray-100 p-2 text-xs whitespace-pre-wrap">
                  {formatJSONish(activity.output.content)}
                </pre>
              </div>
            ) : null}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {renderToolContent()}
      {error ? (
        <ToolActivityError
          error={error}
          hasExecutionDetails={executionDetails !== undefined}
        />
      ) : null}
      {executionDetails ? (
        <div className="space-y-3 border-t border-border-light pt-3 text-xs">
          <ToolCallExecutionDetailsView executionDetails={executionDetails} />
        </div>
      ) : null}
    </>
  );
};

interface ToolActivityInputProps {
  activity: AdminApiRecordingToolActivity;
}

const ToolActivityInput: FC<ToolActivityInputProps> = (props) => {
  const { activity } = props;
  return (
    <div>
      <h4 className="font-semibold">Input</h4>
      <pre className="mt-1 rounded-sm bg-gray-100 p-2 text-xs whitespace-pre-wrap">
        {JSON.stringify(activity.input.tool_arguments, null, 2)}
      </pre>
    </div>
  );
};

/**
 * The agent is only ever told a tool call went wrong, so without this the
 * failure lives in the server logs alone.
 */
const ToolActivityError: FC<{
  error: ToolCallError;
  /** The collapsed request details below already show the full response body. */
  hasExecutionDetails: boolean;
}> = ({ error, hasExecutionDetails }) => (
  <div>
    <h4 className="font-semibold text-red-700">Error</h4>
    <p className="mt-1">
      {error.message}
      {error.response_status ? ` (status ${error.response_status})` : null}
    </p>
    {!hasExecutionDetails && error.response_body ? (
      <pre className="mt-1 rounded-sm bg-gray-100 p-2 text-xs whitespace-pre-wrap">
        {formatJSONish(error.response_body)}
      </pre>
    ) : null}
  </div>
);

function renderToolActivityTitle(
  toolId: string,
  tool: Tool | undefined,
): string {
  if (toolId === KNOWLEDGE_RETRIEVAL_TOOL_ID) {
    return "Retrieve knowledge base document";
  }

  if (toolId === IMPLICIT_END_CALL_TOOL_ID) {
    return IMPLICIT_END_CALL_TOOL_DISPLAY_NAME;
  }

  if (toolId === TOOL_RESULT_QUERY_TOOL_ID) {
    return TOOL_RESULT_QUERY_TOOL_DISPLAY_NAME;
  }

  return tool?.name ?? "(Unknown tool)";
}

function formatQuery(query: unknown): string {
  return typeof query === "string" ? query : "Unknown";
}

/** Pretty-prints a JSON payload, leaving anything that is not JSON as it is. */
function formatJSONish(value: string): string {
  const parsed = safeParseJSON(value);

  return parsed === null ? value : JSON.stringify(parsed, null, 2);
}
